<script setup>
import { ref, onMounted, watch, defineProps } from "vue"
import { KNOLIA } from "@/knolia"
import { Gems } from "../gems"
import { MESSAGES } from "../messages"
import router from "../router"

const props = defineProps({
  userStore: {
    type: Object,
    default: Object
  }
})

const defaultImage = ref(require('@/images/default.png'))
const activeTab = ref('functions-list')
const functionsListData = ref([])
const activitiesListData = ref([])
const showAll = ref(false)
const showAllJobs = ref(false)
const userData = ref({})
const selfPotential = ref(true)

async function fetchLists() {
  const response = await KNOLIA.CallBackEnd("get", "user-potential")

  if(response.error) {
    Gems.Telegram(MESSAGES.ERROR.DEFAULT, 'tele-not-allowed')
    return
  }

  functionsListData.value = response.data.functions_list
  activitiesListData.value = response.data.activities_list
}

watch(showAll, (e) => {
    activitiesListData.value.forEach(activity => {
        activity.showChildren = e ? true : false

        Object.values(activity.children).forEach(task => {
            task.showChildren = e ? true : false

            Object.values(task.children).forEach(skill => {
                skill.showChildren = e ? true : false
            })
        })
    })

})

watch(showAllJobs, (e) => {
    functionsListData.value.forEach(job => {
        job.showChildren = e ? true : false

        Object.values(job.children).forEach(activity => {
            activity.showChildren = e ? true : false

            Object.values(activity.children).forEach(task => {
                task.showChildren = e ? true : false

                Object.values(task.children).forEach(skill => {
                    skill.showChildren = e ? true : false
                })
            })
        })
    })

})

onMounted(async () => {
    selfPotential.value = KNOLIA.GetRequestedUserId() == KNOLIA.GetLoggedUserId()
    if(!selfPotential.value) {
        const response = await KNOLIA.CallBackEnd('get', `get-user`)
        
        response.data.user.accept_validate = Boolean(response.data.user.accept_validate) ?? false
        userData.value = response.data.user
    } else {
        userData.value = props.userStore.User
    }

  fetchLists()
});
</script>

<template>
    <div>
        <div class="tab-container">
            <div class="tab" :class="{'tab-active': activeTab === 'functions-list'}" @click="activeTab = 'functions-list'">
                <span>Funções em potencial</span>
            </div>
            <div class="tab" :class="{'tab-active': activeTab === 'activities-list'}" @click="activeTab = 'activities-list'">
                <span>Atividades em potencial</span>
            </div>
        </div>

        <div class="page-container">

            <div id="div-user-header">
                <img class="img-user-photo" :src="userData.src ?? defaultImage" @error="$event.target.src = defaultImage">

                <div>
                    <div id="div-name" class="line-clamp line-clamp-1 fw-bold">{{ userData.complete_name }}</div>
                    
                    <div class="line-clamp line-clamp-1">Nº Interno: <strong>{{ userData.user_internal }}</strong></div>

                    <div id="div-teams">
                        Equipa(s): 
                        <span class="fw-bold" v-if="userData.assigned_teams?.length == 0">[sem equipa]</span>
                        <span class="fw-bold" v-else-if="userData.assigned_teams?.length == 1" v-for="team in userData.assigned_teams" :key="team">{{ team.designation }}</span>
                        <span class="fw-bold" v-else>{{ userData.assigned_teams?.length }} equipas</span>
                    </div>
                </div>

                <i class="fas fa-arrow-left go-back-icon cursor-pointer" @click="router.go(-1)" @mouseenter="Gems.Tooltip($event.target, 'Retroceder', 'tooltip')"></i>
            </div>

            <div id="div-separation-line"></div>

            <div v-if="activeTab === 'functions-list'">
                <div class="div-tab-title-container">
                    <div class="line-clamp line-clamp-1">Funções em potencial</div>
                    <div class="show-all-container cursor-pointer" @click="showAllJobs = !showAllJobs" v-if="functionsListData.length > 0">
                        <span>Expandir/Fechar tudo</span>
                        <i class="icon fas" :class="{ 'fa-plus-square': !showAllJobs, 'fa-minus-square': showAllJobs }" />
                    </div>
                </div>

                <div v-if="functionsListData.length == 0">
                    <div class="div-element div-job cursor-default justify-content-between">
                        <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                            <span>Sem funções em potencial</span>
                        </div>
                    </div>
                </div>

                <div v-for="job in functionsListData" :key="job.id" >
                    <div class="div-element div-job cursor-default justify-content-between">
                        <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                            <span @mouseenter="Gems.Tooltip($event.target, 'Função', 'tooltip')">{{ job.designation }}</span>
                        </div>
                        <i class="fas cursor-pointer skills" @click="job.showChildren = !job.showChildren"
                            :class="{ 'fa-chevron-down': !job.showChildren, 'fa-chevron-up': job.showChildren }"></i>
                    </div>
                    
                    <div class="div-op-skills-container" v-show="job.showChildren">
                        <div v-for="activity in job.children" :key="activity.id">
                            <div class="div-element div-activity cursor-default justify-content-between">
                                <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                    <span @mouseenter="Gems.Tooltip($event.target, 'Atividade', 'tooltip')">{{ activity.designation }}</span>
                                </div>
                                <span class="text-end" v-if="activity.children.length == 0">[Sem Tarefas Associadas]</span>
                                <span class="text-end" v-else>{{ activity.projected_participation_level }}</span>
                                <i class="fas cursor-pointer skills" @click="activity.showChildren = !activity.showChildren"
                                    :class="{ 'fa-chevron-down': !activity.showChildren, 'fa-chevron-up': activity.showChildren }"></i>
                                
                            </div>

                            <div class="div-op-skills-container" v-show="activity.showChildren">
                                <div v-for="task in activity.children" :key="task.id">
                                    <div class="div-element div-task cursor-default justify-content-between">
                                        <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                            <span @mouseenter="Gems.Tooltip($event.target, 'Tarefa', 'tooltip')">{{ task.designation }}</span>
                                        </div>
                                        <span class="text-end" v-if="task.children.length == 0">[Sem Competências Associadas]</span>
                                        <span class="text-end" v-else>{{ task.projected_participation_level }}</span>
                                        <i class="fas cursor-pointer skills" @click="task.showChildren = !task.showChildren" v-if="Object.keys(task.children).length > 0"
                                           :class="{ 'fa-chevron-down': !task.showChildren, 'fa-chevron-up': task.showChildren }"></i>
                                    </div>

                                    <div class="div-op-skills-container" v-show="task.showChildren">
                                        <div class="div-element div-op-skill cursor-default" v-for="opSkill in task.children" :key="opSkill.id">
                                            <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                                <span @mouseenter="Gems.Tooltip($event.target, 'Competência Operativa', 'tooltip')">{{ opSkill.designation }}</span>
                                            </div>
                                            <span class="text-end">{{ opSkill.proficiency_desig ?? '--' }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                

            </div>

            <div v-if="activeTab === 'activities-list'">
                <div class="div-tab-title-container">
                    <div class="line-clamp line-clamp-1">Atividades em potencial</div>
                    <div class="show-all-container cursor-pointer" @click="showAll = !showAll" v-if="activitiesListData.length > 0">
                        <span>Expandir/Fechar tudo</span>
                        <i class="icon fas" :class="{ 'fa-plus-square': !showAll, 'fa-minus-square': showAll }" />
                    </div>
                </div>

                <div v-if="activitiesListData.length == 0">
                    <div class="div-element div-job cursor-default justify-content-between">
                        <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                            <span>Sem atividades em potencial</span>
                        </div>
                    </div>
                </div>

                <div v-for="activity in activitiesListData" :key="activity.id">
                    <div class="div-element div-activity cursor-default justify-content-between">
                        <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                            <span @mouseenter="Gems.Tooltip($event.target, 'Atividade', 'tooltip')">{{ activity.designation }}</span>
                        </div>
                        <span class="text-end">{{ activity.projected_participation_level }}</span>
                        <i class="fas cursor-pointer skills" @click="activity.showChildren = !activity.showChildren"
                            :class="{ 'fa-chevron-down': !activity.showChildren, 'fa-chevron-up': activity.showChildren }"></i>
                        
                    </div>

                    <div class="div-op-skills-container" v-show="activity.showChildren">
                        <div v-for="task in activity.children" :key="task.id">
                            <div class="div-element div-task cursor-default justify-content-between">
                                <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                    <span @mouseenter="Gems.Tooltip($event.target, 'Tarefa', 'tooltip')">{{ task.designation }}</span>
                                </div>
                                <span class="text-end" v-if="task.children.length == 0">[Sem Competências Associadas]</span>
                                <span class="text-end" v-else>{{ task.projected_participation_level }}</span>
                                <i class="fas cursor-pointer skills" @click="task.showChildren = !task.showChildren" v-if="Object.keys(task.children).length > 0"
                                    :class="{ 'fa-chevron-down': !task.showChildren, 'fa-chevron-up': task.showChildren }"></i>
                            </div>

                            <div class="div-op-skills-container" v-show="task.showChildren">
                                <div class="div-element div-op-skill cursor-default" v-for="opSkill in task.children" :key="opSkill.id">
                                    <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                        <span @mouseenter="Gems.Tooltip($event.target, 'Competência Operativa', 'tooltip')">{{ opSkill.designation }}</span>
                                    </div>
                                    <span class="text-end">{{ opSkill.proficiency_desig ?? '--' }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

    /* USER DATA */
    #div-teams {
        display: flex;
        gap: 5px;
        cursor: default;
    }

    .div-team + * {
        padding-left: 10px;
        border-left: 1px solid black;
    }

    #div-user-content {
        background-color: white;
        padding: 20px;
        width: 50%;
        box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
        border-radius: 5px;
    }

    #div-user-header {
        font-size: 1.3em;
        display: flex;
        align-items: center;
        gap: 1%;
        margin-bottom: 1%;
    }

    #div-separation-line {
        border-top: 1px solid;
        margin: 5px 0px;
    }

    #div-name {
        display: flex;
        justify-content: space-between;
    }

    .img-user-photo {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background-size: cover;
        background-position: center;
        object-fit: cover;
        margin: 15px;
    }

    .go-back-icon {
        font-size: 5rem;
        right: 0;
        position: absolute;
        margin-right: 40px;
    }

    /* ************** */
    
    
    .tab-container {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid #06071452;
    }

    .tab {
        display: flex;
        justify-content: center;
        text-transform: uppercase;
    }

    .tab div:nth-child(3) {
        margin-left: 20px;
    }

    .div-element {
        padding: 5px 15px 5px 15px;
        margin-top: 2px;
        display: grid;
        gap: 5px;
        align-items: center;
    }

    .div-op-skills-container {
        padding-left: 20px;
    }

    .div-job {
        background-color: var(--default-app-alt-line-color);
        grid-template-columns: 98% 2%; 
        gap: 10px;
    }

    .div-activity {
        background-color: #3f739f;
        grid-template-columns: 40% 58% 2%; 
    }

    .div-task {
        background-color: #405f7b;
        grid-template-columns: 40% 58% 2%; 
    }

    .div-op-skill {
        grid-template-columns: 40% 58% 2%; 
    }

    .show-all-container {
        display: flex;
        align-items: center;
        gap: 10px;
        justify-content: end;
    }
    
    .div-tab-title-container {
        font-size: 1.2em;
        font-weight: bold;
        text-transform: uppercase;
        display: flex;
        justify-content: space-between;
        margin: 10px 0px;
    }

    @media screen and (max-width: 768px) {

        #div-separation-line {
            margin: 0px 2%;
        }

        #div-user-header {
            font-size: 1.1em;
            gap: 2%;
            margin-bottom: 0%;
            padding: 2%;        
        }

        .div-tab-title-container {
            padding: 2% 2% calc(2% - 1px) 2%;
        }

        .img-user-photo {
            width: 65px;
            height: 65px;
        }

        .tab-container {
            width: 100%;
        }
    }
</style>